import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonService } from 'src/app/services/Common-function/common.service';

/**
 * Beat22 Diwali Sale: Pro Annual offer popup (shared).
 * Left: firework fountain + "45% OFF" callout; right: price, countdown to the sale end, benefits and one CTA.
 * The parent decides what the CTA does (subscription component: address → payment gateway, or the upgrade flow)
 * and closes the dialog on (closed).
 * Usage: <ipm-diwali-pro-popup [isUpgrade]="true" (claim)="..." (closed)="..." (compare)="..."></ipm-diwali-pro-popup>
 */
@Component({
  selector: 'ipm-diwali-pro-popup',
  templateUrl: './diwali-pro-popup.component.html',
  styleUrls: ['./diwali-pro-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiwaliProPopupComponent implements OnInit, AfterViewInit, OnDestroy {
  /** User already has a paid plan: CTA reads "Upgrade" instead of "Claim Diwali Offer" */
  @Input() isUpgrade: boolean = false;
  @Output() claim = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  /** "Compare plans" clicked: the parent opens the subscriptions page */
  @Output() compare = new EventEmitter<void>();

  @ViewChild('fireworksCanvas', { static: false }) canvasRef: ElementRef<HTMLCanvasElement>;

  readonly proPlanPrice: number = 4999;
  readonly proPlanSalePrice: number = 3999;
  readonly saleOff: number = 45;
  readonly benefits: Array<string> = [
    'Sell Unlimited Beats',
    'Sell Unlimited Sound Kits',
    '10% Commission per Sale',
    '20% off Beat22 Campaigns',
    '₹1,500 Ad Wallet Balance',
    '5,000 Kits22 & Labs Credits',
    'Lead Generation Access',
    'Priority Support',
    'Digital Licensing',
    'Direct Bank Transfer',
  ];
  countdown: { d: string; h: string; m: string; s: string } | null = null;

  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private frameId = 0;
  private resizeObserver: ResizeObserver | null = null;

  constructor(
    public commonService: CommonService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.updateCountdown();
    this.countdownTimer = setInterval(() => this.updateCountdown(), 1000);
  }

  ngAfterViewInit(): void {
    const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!reduceMotion && this.canvasRef) {
      this.zone.runOutsideAngular(() => this.startFireworks(this.canvasRef.nativeElement));
    }
  }

  ngOnDestroy(): void {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    cancelAnimationFrame(this.frameId);
    this.resizeObserver?.disconnect();
  }

  onClaim(): void {
    this.claim.emit();
  }

  close(): void {
    this.closed.emit();
  }

  /** Time left until the platform sale ends; hidden when there is no end date */
  private updateCountdown(): void {
    const endAt = this.commonService.getActivePlatformSale()?.end_at;
    if (!endAt) {
      this.countdown = null;
    } else {
      const left = Math.max(0, new Date(endAt).getTime() - Date.now());
      const pad = (n: number) => String(Math.floor(n)).padStart(2, '0');
      this.countdown = {
        d: pad(left / 86400000),
        h: pad((left % 86400000) / 3600000),
        m: pad((left % 3600000) / 60000),
        s: pad((left % 60000) / 1000),
      };
    }
    this.cdr.markForCheck();
  }

  /**
   * Diwali fireworks on the left panel: a fountain from the bottom plus scheduled bursts.
   * The canvas uses mix-blend-mode: screen, so its black fill disappears over the panel gradient.
   */
  private startFireworks(canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const colors = ['#FFD700', '#FFEC6A', '#F5C842', '#E8A820', '#FF8C00', '#FF6B00', '#FF4500', '#FFB347',
      '#FFDEAD', '#FFE680', '#C9902A', '#E07B20', '#FFF0A0', '#FFA040', '#D4A017'];
    let width = 0;
    let height = 0;
    const resize = () => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      canvas.width = width = Math.round(rect?.width || 200);
      canvas.height = height = Math.round(rect?.height || 400);
    };
    resize();
    if (typeof ResizeObserver !== 'undefined' && canvas.parentElement) {
      this.resizeObserver = new ResizeObserver(resize);
      this.resizeObserver.observe(canvas.parentElement);
    }

    // fixed-size particle pool
    const count = 280;
    const px = new Float32Array(count), py = new Float32Array(count);
    const vx = new Float32Array(count), vy = new Float32Array(count);
    const life = new Float32Array(count), maxLife = new Float32Array(count);
    const size = new Float32Array(count), color = new Int16Array(count);
    const alive = new Uint8Array(count);
    const emit = (x: number, y: number, dx: number, dy: number, lifespan: number, radius: number) => {
      for (let i = 0; i < count; i++) {
        if (!alive[i]) {
          px[i] = x; py[i] = y; vx[i] = dx; vy[i] = dy;
          life[i] = 0; maxLife[i] = lifespan; size[i] = radius;
          color[i] = Math.floor(Math.random() * colors.length);
          alive[i] = 1;
          return;
        }
      }
    };
    const fountain = () => {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.62;
      const speed = 1.6 + Math.random() * 3.2;
      emit(width * 0.5 + (Math.random() - 0.5) * 18, height + 2, Math.cos(angle) * speed, Math.sin(angle) * speed,
        55 + Math.random() * 75, 0.7 + Math.random() * 2.5);
    };
    const burst = (x: number, y: number) => {
      const n = 26 + Math.floor(Math.random() * 22);
      for (let b = 0; b < n; b++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 0.6 + Math.random() * 3.6;
        emit(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed, 28 + Math.random() * 48, 0.6 + Math.random() * 2.2);
      }
    };

    let schedule = [90, 210, 350, 490, 620];
    let next = 0;
    let base = 0;
    let frame = 0;
    const loop = () => {
      this.frameId = requestAnimationFrame(loop);
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, width, height);
      if (frame % 2 === 0) fountain();
      if (next < schedule.length && frame - base >= schedule[next]) {
        burst(width * (0.15 + Math.random() * 0.72), height * (0.1 + Math.random() * 0.52));
        next++;
      }
      if (next >= schedule.length) {
        base = frame + 30;
        schedule = schedule.map((_, k) => 90 + k * 130 + Math.floor(Math.random() * 40));
        next = 0;
      }
      frame++;
      for (let i = 0; i < count; i++) {
        if (!alive[i]) continue;
        vx[i] *= 0.988;
        vy[i] += 0.058;
        px[i] += vx[i];
        py[i] += vy[i];
        life[i]++;
        if (life[i] >= maxLife[i] || py[i] > height + 10) { alive[i] = 0; continue; }
        const t = life[i] / maxLife[i];
        ctx.globalAlpha = (1 - t) * (1 - t) * 0.92;
        ctx.fillStyle = colors[color[i]];
        ctx.beginPath();
        ctx.arc(px[i], py[i], size[i] * (1 - t * 0.4), 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };
    loop();
  }
}
